import { useState } from "react";
import { Button, Callout, Dialog, DialogText, useToast, errorText, useCommandMutation } from "@amber/ui";
import { useProfileInvalidation } from "../invalidation";
import { profileCommands } from "../queries";

export function ExportDialog({ shownIds, onClose }: { shownIds: readonly string[]; onClose: () => void }) {
  const toast = useToast();
  const invalidation = useProfileInvalidation();
  const [scope, setScope] = useState<"shown" | "all">("shown");
  const mutation = useCommandMutation({
    run: (csrf) => profileCommands.exportRoster({ scope, ids: scope === "shown" ? [...shownIds] : [] }, csrf),
    ...invalidation,
    onSuccess: (r) => {
      const url = URL.createObjectURL(new Blob([r.csv], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = r.filename;
      a.click();
      URL.revokeObjectURL(url);
      toast.show(`Exported ${String(r.rows)} profile${r.rows === 1 ? "" : "s"}`);
      onClose();
    },
  });
  return (
    <Dialog
      open
      onOpenChange={(o) => {
        if (!o) onClose();
      }}
      title="Export roster (CSV)"
      onSubmit={() => {
        mutation.mutate();
      }}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="primary" aria-busy={mutation.isPending || undefined}>
            Export
          </Button>
        </>
      }
    >
      <fieldset className="m-0 border-0 p-0 text-ui">
        <legend className="sr-only">Which profiles</legend>
        <label className="flex items-center gap-2">
          <input
            type="radio"
            name="scope"
            checked={scope === "shown"}
            onChange={() => {
              setScope("shown");
            }}
          />{" "}
          The {shownIds.length} profile{shownIds.length === 1 ? "" : "s"} shown
        </label>
        <label className="mt-1 flex items-center gap-2">
          <input
            type="radio"
            name="scope"
            checked={scope === "all"}
            onChange={() => {
              setScope("all");
            }}
          />{" "}
          All profiles, including not tracked, drafts and retired
        </label>
      </fieldset>
      <DialogText>
        One row per profile with name, status, role, languages, owner, Wikipedia and Wikidata, sources and last identity
        check; no portraits. The export is recorded in the audit log.
      </DialogText>
      {mutation.error ? (
        <Callout tone="attention" role="alert" className="mt-3">
          {errorText(mutation.error)}
        </Callout>
      ) : null}
    </Dialog>
  );
}
